import {
  LayoutDashboard,
  AlertTriangle,
  ClipboardList,
  Users,
  Settings,
  Bell,
  MapPin,
  BarChart3,
  Building2,
  PanelLeftClose,
  PanelLeft,
  UserPlus,
  ListFilter,
  Image,
  ClipboardCheck,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import LogoMunicipal from "@/assets/logo_municipal.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { useState, useEffect } from "react";

const mainMenuItems = [
  {
    title: "Dashboard",
    url: "/home",
    icon: LayoutDashboard,
    roles: ["ADMIN", "TRIAGE", "AUDIT", "AGENT", "CITIZEN"],
  },
  {
    title: "Fila de Triagem",
    url: "/triage",
    icon: ListFilter,
    roles: ["ADMIN", "TRIAGE", "AUDIT"],
  },
  {
    title: "Ocorrências",
    url: "/occurrences",
    icon: AlertTriangle,
    roles: ["ADMIN", "TRIAGE", "AUDIT", "AGENT"],
  },
  {
    title: "Ordens de Serviço",
    url: "/service-orders",
    icon: ClipboardList,
    roles: ["ADMIN", "AGENT"],
  },
  {
    title: "Validação de OS",
    url: "/work-order-validation",
    icon: ClipboardCheck,
    roles: ["ADMIN", "TRIAGE", "AUDIT"],
  },
  {
    title: "Mapa",
    url: "/map",
    icon: MapPin,
    roles: ["ADMIN", "AGENT", "TRIAGE"],
  },
];

const managementItems = [
  { title: "Setores", url: "/sectors", icon: Building2, roles: ["ADMIN"] },
  { title: "Equipes", url: "/teams", icon: Users, roles: ["ADMIN"] },
  { title: "Usuários", url: "/users", icon: UserPlus, roles: ["ADMIN"] },
  { title: "Banners", url: "/banners", icon: Image, roles: ["ADMIN"] },
  { title: "Notificações", url: "/notificacoes", icon: Bell, roles: ["ADMIN"] },
  {
    title: "Relatórios",
    url: "/relatorios",
    icon: BarChart3,
    roles: ["ADMIN", "AUDIT"],
  },
];

const configItems = [
  {
    title: "Configurações",
    url: "/configuracoes",
    icon: Settings,
    roles: ["ADMIN"],
  },
];

export function SideMenu() {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = isCollapsed;
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const userData = localStorage.getItem("duas-vidas-user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const getUserInitials = () => {
    if (!user?.profile?.fullName) return "U";
    const names = user.profile.fullName.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const isActive = (path: string) => location.pathname === path;

  const userRole = user?.role || "CITIZEN";

  const filterItemsByRole = (items: typeof mainMenuItems) => {
    return items.filter((item) => item.roles.includes(userRole));
  };

  const renderMenuItems = (items: typeof mainMenuItems) => {
    const filteredItems = filterItemsByRole(items);

    if (filteredItems.length === 0) return null;

    return (
      <SidebarMenu>
        {filteredItems.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton asChild isActive={isActive(item.url)}>
              <NavLink
                to={item.url}
                className={`flex items-center gap-2.5 w-full ${collapsed ? "justify-center" : ""}`}
              >
                <item.icon
                  className="shrink-0"
                  style={{
                    width: collapsed ? "20px" : "16px",
                    height: collapsed ? "20px" : "16px",
                  }}
                />
                {!collapsed && <span className="text-sm">{item.title}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="px-4 py-4">
        {collapsed ? (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center justify-center rounded-xl bg-white/95 p-2">
              <img
                src={LogoMunicipal}
                alt="Vida Mais Simples"
                className="w-20 object-contain"
              />
            </div>
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-sidebar-accent/60 transition-all"
              aria-label="Expandir sidebar"
            >
              <PanelLeft className="h-5 w-5 text-gray-300" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex items-center justify-center shrink-0 rounded-xl bg-white/95 p-1.5">
                  <img
                    src={LogoMunicipal}
                    alt="Vida Mais Simples"
                    className="h-14 w-14 object-contain"
                  />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-base font-black text-white leading-tight">
                    Vida Mais Simples
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium">
                    Ceará
                  </span>
                </div>
              </div>
              <button
                onClick={toggleSidebar}
                className="shrink-0 p-1.5 rounded-lg hover:bg-sidebar-accent/60 transition-all"
                aria-label="Recolher sidebar"
              >
                <PanelLeftClose className="h-5 w-5 text-gray-300" />
              </button>
            </div>

            {/* Informação institucional */}
            <div className="px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">
                Sistema de Gestão
              </p>
              <p className="text-xs text-white font-semibold">
                Painel Administrativo
              </p>
            </div>
          </div>
        )}
      </SidebarHeader>
      <div className="w-full bg-gray-500/30 h-0.5"></div>

      <SidebarContent className="px-4 py-7">
        <SidebarGroup>
          {!collapsed && (
            <div className="mb-2">
              <SidebarGroupLabel className="text-gray-300 text-[10px] font-bold uppercase tracking-wider px-2 flex items-center gap-2">
                Principal
              </SidebarGroupLabel>
            </div>
          )}
          <SidebarGroupContent className="text-2xl">
            {renderMenuItems(mainMenuItems)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-4">
          {!collapsed && (
            <div className="mb-2">
              <SidebarGroupLabel className="text-gray-300 text-[10px] font-bold uppercase tracking-wider px-2 flex items-center gap-2">
                Gestão
              </SidebarGroupLabel>
            </div>
          )}
          <SidebarGroupContent>
            {renderMenuItems(managementItems)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-4">
          {!collapsed && (
            <div className="mb-2">
              <SidebarGroupLabel className="text-gray-300 text-[10px] font-bold uppercase tracking-wider px-2 flex items-center gap-2">
                Sistema
              </SidebarGroupLabel>
            </div>
          )}
          <SidebarGroupContent>
            {renderMenuItems(configItems)}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <div className="w-full bg-gray-500/30 h-0.5"></div>
      <SidebarFooter className="py-2">
        <div
          className={`flex w-full items-center rounded-lg p-2 relative overflow-hidden ${collapsed ? "justify-center" : "gap-2"}`}
        >
          <Avatar className="h-8 w-8 ring-2 ring-[hsl(var(--primary))]">
            <AvatarImage src="" />
            <AvatarFallback className="bg-[hsl(var(--secondary))] text-white text-xs font-bold">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>

          {!collapsed && (
            <div className="flex flex-col items-start text-left flex-1 min-w-0">
              <span className="text-xs font-semibold text-white truncate">
                {user?.profile?.fullName || "Usuário"}
              </span>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[10px] text-gray-400 truncate">
                  {user?.email || "email@exemplo.com"}
                </span>
              </div>
            </div>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
